import React, { useMemo } from "react";
import "@egjs/react-flicking/dist/flicking.css";
import { CardButton, PostCard } from "./styles";
import { IoCloseSharp } from "react-icons/io5";
import { IoMdCheckmark } from "react-icons/io";
import ReactCardFlip from "react-card-flip";
import { CardProps } from "./types";
import ThemeColors from "../../../../../../../../themes/colors";
import { Column, Row } from "../../../../../../../../themes/globalStyle";
import Typography from "../../../../../../../../components/Typography";
import SectionItem from "./components/SectionItem";
import usePost from "../../../../hooks/usePost";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import { postService } from "../../../../../../../../services/modules/post";
import { notification } from "antd";
import { Status } from "../../../../types";
import useEnterprise from "../../../../../../hooks/useEnterprise";

dayjs.locale("pt-br");

const Card: React.FC<CardProps> = ({
  index,
  isFlipped,
  post,
  handleCardFlip,
}): JSX.Element => {
  const { enterpriseState } = useEnterprise();
  const { postState, fetchPosts } = usePost();

  const defineCardColor = useMemo(() => {
    const group = postState.groups.filter(
      (group) => group.smartGoalId === post.smart_goal_id
    );

    if (group && group[0]) {
      return group[0].color;
    }

    return ThemeColors.Purple500;
  }, [postState.groups, post]);

  const smartGoal = useMemo(() => {
    const foundSmartGoal = postState.smartGoals?.filter(
      (smartGoal) => smartGoal._id === post.smart_goal_id
    );

    if (foundSmartGoal && foundSmartGoal[0]) return foundSmartGoal[0];
  }, [post, postState.smartGoals]);

  const changePostStatus = (
    status: Status,
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.stopPropagation();

    if (post._id && enterpriseState.selectedEnterprise) {
      postService.updatePost(post._id, status).then(() => {
        enterpriseState.selectedEnterprise &&
          fetchPosts(enterpriseState.selectedEnterprise);

        notification.success({
          message: "Sucesso",
          description: `Postagem atualizada com sucesso!`,
        });
      });
    }
  };

  return (
    <ReactCardFlip isFlipped={!!isFlipped} flipDirection="horizontal">
      <PostCard color={defineCardColor} onClick={() => handleCardFlip(index)}>
        <Row>
          <Typography
            text="clique para ver mais detalhes"
            size="tiny"
            color="Bg300"
            weight={100}
          />
        </Row>
        <Row>
          <Typography
            text={dayjs(post.date).format("DD")}
            size="doubleExtraHuge"
            color="Bg300"
            weight={700}
          />
          <Column style={{ gap: "0.5rem" }}>
            <Column style={{ gap: 0 }}>
              <Typography text="de" size="large" color="Bg300" weight={100} />
              <Typography
                text={dayjs(post.date).format("MMMM")}
                size="large"
                color="Bg300"
                weight={100}
              />
            </Column>
            <Typography
              text={dayjs(post.date).format("dddd")}
              size="tiny"
              color="Bg300"
              weight={100}
            />
          </Column>
        </Row>
        <Row>
          <Column style={{ textAlign: "center" }}>
            <Typography
              text={post.conscious_level}
              size="medium"
              color="Bg300"
              weight={500}
            />
            <Typography
              text={post.conscious_level_description}
              size="small"
              color="Bg300"
              weight={100}
            />
          </Column>
        </Row>
        <Row style={{ gap: "1rem", marginTop: "3rem" }}>
          <CardButton
            onClick={(e) => changePostStatus("deleted", e)}
            customColor={
              post.status === "deleted" ? ThemeColors.Red100 : undefined
            }
          >
            <IoCloseSharp color={ThemeColors.Red500} />
          </CardButton>
          <CardButton
            onClick={(e) =>
              post.status === "null" && changePostStatus("checked", e)
            }
            customColor={
              post.status === "checked" ? ThemeColors.Green100 : undefined
            }
          >
            <IoMdCheckmark color={ThemeColors.Green500} />
          </CardButton>
        </Row>
      </PostCard>
      <PostCard color={defineCardColor} onClick={() => handleCardFlip(index)}>
        <Row>
          <Typography
            text={post.conscious_level}
            size="large"
            color="Bg300"
            weight={500}
          />
        </Row>
        <Column style={{ overflow: "auto", gap: "1.5rem" }}>
          <SectionItem title={"OBJETIVO"} description={smartGoal?.s || ""} />
          <SectionItem
            title={"PERSONA"}
            description={
              `${postState.persona?.profile.name}, ${postState.persona?.profile.age}, ${postState.persona?.profile.profession}` ||
              ""
            }
          />
          <SectionItem title={"DOR"} description={post.problem || ""} />
          <SectionItem title={"SOLUÇÃO"} description={post.solution || ""} />
          <SectionItem
            title={"TÓPICO"}
            description={post.specific_topic || ""}
          />
          <SectionItem
            title={"PALAVRAS CHAVE"}
            description={(post.keyword as string) || ""}
          />
        </Column>
      </PostCard>
    </ReactCardFlip>
  );
};

export default Card;
